import type { ControlCenter, ControlSignalStatus } from "@/features/admin";
import {
  countryName as displayCountryName,
  languageName as displayLanguageName,
  regionFlag,
} from "@/features/utils/display-names";

/** A country's flag from its ISO alpha-2 code — a globe when there is none. */
export const flag = (code: string): string => regionFlag(code) || "🌐";

// The admin is French-only: names in French, whatever the browser says.
export const countryName = (code: string): string =>
  /^[A-Z]{2}$/.test(code) ? displayCountryName(code, "fr") : "Inconnu";

export const languageName = (code: string): string =>
  code && code !== "?" ? displayLanguageName(code, "fr") : "Inconnue";

/** "4 min 07", "38 s", "1 h 12" — a clock people read at a glance, not a duration spec. */
export const clock = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${String(s % 60).padStart(2, "0")}`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`;
};

export const ago = (at: number, now: number): string => {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
};

/** What a trend arrow is measured against — "today" is compared with yesterday, not last night. */
export const compareLabel = (compare: ControlCenter["compare"]): string =>
  compare === "yesterday"
    ? "Par rapport à hier à la même heure"
    : "Par rapport à la période précédente";

export const percent = (part: number, whole: number, digits = 0): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(digits)} %` : "—";

export const compact = (n: number): string =>
  new Intl.NumberFormat("fr-FR", {
    notation: n >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(n);

/** Status → the token that draws it, so a dot, a bar and a map agree on what red means. */
export const STATUS_VAR: Record<ControlSignalStatus, string> = {
  ok: "var(--success)",
  warn: "var(--warning)",
  bad: "var(--danger)",
  unknown: "var(--muted-foreground)",
};

export const STATUS_LABEL: Record<ControlSignalStatus, string> = {
  ok: "Tout est au vert",
  warn: "Service dégradé",
  bad: "Incident en cours",
  unknown: "Pas assez de données",
};

export const SOURCE_LABEL: Record<string, string> = {
  call_inbound: "Appels entrants",
  call_outbound: "Appels sortants",
  call_web: "Appels depuis le site",
  web_chat: "Chat du site",
  chat: "SMS / messagerie",
  email: "E-mail",
};

export const RESULT_LABEL: Record<string, string> = {
  success: "Réussi",
  failure: "Échec",
  no_answer: "Sans réponse",
  voicemail: "Messagerie vocale",
  unknown: "Non qualifié",
};

export const SENTIMENT_EMOJI: Record<string, string> = {
  positive: "😊",
  neutral: "😐",
  negative: "😟",
};
